import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Fade } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const Static404: React.FC = () => {
  const [showContent, setShowContent] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    // Fade in content after mount
    setTimeout(() => setShowContent(true), 100);
  }, []);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark 
          ? 'linear-gradient(135deg, #0e0e0e 0%, #191919 50%, #2e2e30 100%)'
          : 'linear-gradient(135deg, #F5F6FA 0%, #E8E2F3 50%, #F0E6F7 100%)',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: '"Nunito Sans", sans-serif',
      }}
    >
      {/* Animated background circles */}
      <Box
        sx={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(27,70,114,0.15) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(27,70,114,0.08) 0%, transparent 70%)',
          top: '-200px',
          left: '-200px',
          animation: 'float 20s ease-in-out infinite',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(96,82,162,0.15) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(96,82,162,0.08) 0%, transparent 70%)',
          bottom: '-150px',
          right: '-150px',
          animation: 'float 15s ease-in-out infinite reverse',
        }}
      />

      <Container maxWidth="md">
        <Fade in={showContent} timeout={1000}>
          <Box
            sx={{
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
              py: 4,
            }}
          >
            {/* Error Icon */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: '100px', sm: '120px', md: '140px' },
                height: { xs: '100px', sm: '120px', md: '140px' },
                borderRadius: '50%',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(27,70,114,0.2) 0%, rgba(96,82,162,0.2) 100%)'
                  : 'linear-gradient(135deg, rgba(27,70,114,0.1) 0%, rgba(96,82,162,0.1) 100%)',
                backdropFilter: 'blur(10px)',
                border: isDark ? '2px solid rgba(255,255,255,0.1)' : '2px solid rgba(27,70,114,0.2)',
                mb: 4,
                animation: 'pulse 2s ease-in-out infinite',
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: { xs: '50px', sm: '60px', md: '70px' },
                  color: isDark ? '#FFFFFF' : '#1B4672',
                }}
              />
            </Box>

            {/* 404 Text */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '5rem', sm: '7rem', md: '9rem' },
                fontWeight: 700,
                fontFamily: '"Nunito Sans", sans-serif',
                background: isDark
                  ? 'linear-gradient(135deg, #FFFFFF 0%, #2A5A8A 100%)'
                  : 'linear-gradient(135deg, #1B4672 0%, #6052A2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 2,
                lineHeight: 1,
                letterSpacing: '-0.02em',
              }}
            >
              404
            </Typography>

            {/* Main Message */}
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 600,
                fontFamily: '"Nunito Sans", sans-serif',
                color: isDark ? '#FFFFFF' : '#202224',
                mb: 2,
              }}
            >
              Page Not Found
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                fontWeight: 400,
                fontFamily: '"Nunito Sans", sans-serif',
                color: isDark ? 'rgba(255,255,255,0.7)' : '#666666',
                mb: 5,
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              The page you're looking for doesn't exist or has been moved.
              <br />
              Let's get you back on track.
            </Typography>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '30px',
                  px: 4,
                  py: 1.5,
                  minWidth: '200px',
                  backgroundColor: isDark ? '#FFFFFF' : '#1B4672',
                  color: isDark ? '#191919' : '#FFFFFF',
                  boxShadow: isDark
                    ? '0 4px 20px rgba(255,255,255,0.2)'
                    : '0 4px 20px rgba(27,70,114,0.3)',
                  '&:hover': {
                    backgroundColor: isDark ? '#F0F0F0' : '#2A5A8A',
                    transform: 'translateY(-2px)',
                    boxShadow: isDark
                      ? '0 6px 25px rgba(255,255,255,0.3)'
                      : '0 6px 25px rgba(27,70,114,0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Go to Homepage
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleGoBack}
                sx={{
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '30px',
                  px: 4,
                  py: 1.5,
                  minWidth: '200px',
                  borderColor: isDark ? '#FFFFFF' : '#1B4672',
                  color: isDark ? '#FFFFFF' : '#1B4672',
                  borderWidth: '2px',
                  '&:hover': {
                    borderColor: isDark ? '#F0F0F0' : '#2A5A8A',
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(27,70,114,0.1)',
                    borderWidth: '2px',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Go Back
              </Button>
            </Box>

            {/* Help Text */}
            <Box
              sx={{
                mt: 6,
                p: 3,
                borderRadius: '16px',
                background: isDark
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(27,70,114,0.05)',
                backdropFilter: 'blur(10px)',
                border: isDark
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(27,70,114,0.1)',
                maxWidth: '500px',
                mx: 'auto',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  fontFamily: '"Nunito Sans", sans-serif',
                  color: isDark ? 'rgba(255,255,255,0.6)' : '#666666',
                  lineHeight: 1.6,
                }}
              >
                Need help? Contact our support team at{' '}
                <Box
                  component="a"
                  href="mailto:support@leadforge.me"
                  sx={{
                    color: isDark ? '#FFFFFF' : '#1B4672',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  support@leadforge.me
                </Box>
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Container>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: translate(0, 0) scale(1);
            }
            33% {
              transform: translate(30px, -30px) scale(1.1);
            }
            66% {
              transform: translate(-20px, 20px) scale(0.9);
            }
          }

          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.05);
              opacity: 0.8;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default Static404;
